import { getExtraHours, calcExtraHoursTotal } from "./extraHoursBilling";
import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";

// Status list fija (no depende de los registros)
const FIXED_STATUS_OPTIONS = [
  "All",
  "Not Assigned",
  "Assigned",
  "In Process",
  "In Hold",
  "Correction",
  "Ready for QC",
  "Billing",
  "Payed",
  "Archived",
];

// Excluir estados para cálculos de resumen (Mostrar resumen)
const EXCLUDED_STATUS_FOR_SUMMARY = new Set(["billing", "payed", "archived", "pre-archived", "in hold"]);
const filterOutExcludedSummary = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => {
    const s = String(r?.jobsta ?? "").trim().toLowerCase();
    return !EXCLUDED_STATUS_FOR_SUMMARY.has(s);
  });

// Excluir estados solo para el XLS descargado
const EXCLUDED_STATUS_FOR_XLS = new Set(["billing", "payed", "archived", "pre-archived"]);
const filterOutExcludedXLS = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => {
    const s = String(r?.jobsta ?? "").trim().toLowerCase();
    return !EXCLUDED_STATUS_FOR_XLS.has(s);
  });


/* ================== Axios base (igual que GridPage) ================== */
Axios.defaults.baseURL = "https://backend.sansfrontieres-design.com";
Axios.defaults.withCredentials = false;

/* ================= Helpers (copiados de GridPage) ================= */
const normalize = (v) => (v ?? "").toString().toLowerCase().replace(/[\s-]+/g, "");

function formatDateTimeHM(value) {
  if (!value) return "";
  const toHM = (d) =>
    d.toLocaleString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  const dt = new Date(value);
  if (!isNaN(dt)) return toHM(dt);
  const m = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!m) return String(value);
  const [, Y, M, D, h = "00", i = "00"] = m;
  const dt2 = new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(i), 0);
  if (isNaN(dt2)) return String(value);
  return toHM(dt2);
}



// Devuelve fecha local YYYY-MM-DD a partir de un Date o string tipo 'YYYY-MM-DD HH:mm:ss'
function getDateKey(value) {
  if (!value) return "";
  if (value instanceof Date && !isNaN(value)) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const dt = new Date(value);
  if (!isNaN(dt)) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : s.slice(0, 10);
}

/* ================= Cálculos reutilizables (para cualquier parte del programa) ================= */
export const n = (v) => {
  const x = parseFloat(String(v ?? "").replace(",", "."));
  return isNaN(x) ? 0 : x;
};

export const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// Equivalencias de Type para cálculos de reportes.
// Solo cambia cómo se interpreta el Type al calcular; no altera el valor guardado en BD.
const normalizeCalcJobType = (value) => {
  const t = normalize(value);
  switch (t) {
    case "coaxbentleyasb": return "coaxasbuilt";
    case "coaxbentleydes": return "coaxdesign";
    case "fiberbentleyasb": return "fiberasbuiltbau";
    case "fiberbentleydes": return "fiberasbuiltbau";
    default: return t;
  }
};


// Types que NO cobran LODE base en la fórmula genérica de Main/Main-SFD.
// Además, el recargo genérico por LODE > 2000 fue eliminado para TODOS los Types.
const NO_GENERIC_LODE_BASE_TYPES = new Set([
  "coaxasbuilt",
  "coaxdesign",
  "coaxbentleyasb",
  "coaxbentleydes",
  "fiberbentleyasb",
  "fiberbentleydes",
  "route",
  "routefttp",
  "fiberfttp",
]);

const usesGenericLodeBase = (r) => !NO_GENERIC_LODE_BASE_TYPES.has(normalize(r?.jobti));

const isBentleySplitCoaxAsbuilt = (r) => normalize(r?.jobti) === "bentleysplitcoaxasbuilt";
const isBentleySplitFiberAsbuilt = (r) => normalize(r?.jobti) === "bentleysplitfiberasbuilt";

const isProject01 = (r) => String(r?.jobpro ?? "").trim() === "Project-01";
const isFiberDesignBAU = (r) => normalize(r?.jobti) === "fiberdesignbau";
const isFiberAsbuiltBAU = (r) => {
  const t = normalize(r?.jobti);
  return t === "fiberasbuiltbau" || t === "fiberbentleyasb";
};
const isCoaxAsbuilt = (r) => {
  const t = normalize(r?.jobti);
  return t === "coaxasbuilt" || t === "coaxbentleyasb";
};

// Route / Route FTTP: SOLO procesan Route.
// NO Coax, NO Asbuilt, NO LODE, NO HC.
const isRouteOnlyType = (r) => {
  const t = normalize(r?.jobti);
  return t === "route" || t === "routefttp";
};

const isCoaxAsbRevertSplit = (r) => normalize(r?.jobti) === "coaxasbrevertsplit";
const isCoaxAsbRevertN0 = (r) => normalize(r?.jobti) === "coaxasbrevertn+0";
const isIrisSplitCoaxAsbuilt = (r) => normalize(r?.jobti) === "irissplitcoaxasbuilt";

// Coax Asb Revert Split - regla nueva:
// Main = 160 fijo.
// Main-SFD = 12.5 + $0.50 por cada HC/AMP por encima de 20.
const coaxAsbRevertSplitSfdTotal = (hc) => {
  const value = n(hc);
  return r2(12.5 + Math.max(0, value - 20) * 0.5);
};

// Main: devuelve AL (suma AC..AK) según modo
// Add the separately priced hours exactly once, preserving every base-price rule.
export function calcMainAL(r, mode) {
  return r2(calcMainBase(r, mode) + calcExtraHoursTotal(r, mode === "ADV" ? "SFD" : "CLIENT"));
}
export function calcBentleyTotal(r, mode) {
  return r2(calcBentleyBase(r, mode) + calcExtraHoursTotal(r, mode === "BENTLEY_SFD" ? "SFD" : "CLIENT"));
}

function calcMainBase(r, mode /* "SFD" | "ADV" */) {
  const routeA = n(r?.jobrouteaerial);
  const routeU = n(r?.jobrouteug);
  const coaxA = n(r?.jobcoaxae);
  const coaxU = n(r?.jobcoaxug);
  const asbA = n(r?.jobasbae);
  const asbU = n(r?.jobasbug);
  const lode = n(r?.joblode);
  const hc = n(r?.jobhc);
  const jobType = normalizeCalcJobType(r?.jobti);

  // Reglas específicas de Coax Asb Revert.
  // Se evalúan antes de Project-01 porque sus precios son propios y no dependen de la regla genérica.
  if (isCoaxAsbRevertSplit(r)) {
    if (mode === "ADV") return coaxAsbRevertSplitSfdTotal(hc);
    return r2(160);
  }

  // Coax Asb Revert N+0: precio total fijo, sin condiciones ni componentes adicionales.
  if (isCoaxAsbRevertN0(r)) {
    if (mode === "ADV") return r2(10);
    return r2(20);
  }

  // Iris Split Coax Asbuilt:
  // Main = 105 fijo.
  // Main-SFD = misma regla que Coax Asb Revert Split.
  if (isIrisSplitCoaxAsbuilt(r)) {
    if (mode === "ADV") return coaxAsbRevertSplitSfdTotal(hc);
    return r2(105);
  }

  if (mode === "ADV" && isProject01(r)) return 0;

  // Route / Route FTTP - regla exclusiva de Route:
  // MAIN: SOLO Route(Main). NO Coax, NO Asbuilt, NO LODE, NO HC.
  // MAIN-SFD: SOLO Route(Main-SFD). NO Coax, NO Asbuilt, NO LODE, NO HC.
  if (isRouteOnlyType(r)) {
    const totalRoute = routeA + routeU;

    if (mode === "ADV") {
      if (totalRoute > 20000) return r2(totalRoute * 0.008);
      let routeTotal = totalRoute > 0 ? r2(10) : 0;
      if (totalRoute > 2000) routeTotal += r2(((totalRoute - 2000) / 5280) * 12.5);
      return r2(routeTotal);
    }

    if (totalRoute > 20000) return r2(totalRoute * 0.008);
    let routeTotal = totalRoute > 0 ? r2(30.25) : 0;
    if (totalRoute > 2000) routeTotal += r2((totalRoute - 2000) * 0.008);
    return r2(routeTotal);
  }

  // Coax Asbuilt / Coax Bentley Asb - regla vigente:
  // MAIN: SOLO Total Asbuilt. NO Route, NO Coax, NO LODE, NO HC.
  // MAIN-SFD: SOLO Total Asbuilt. NO Route, NO Coax, NO LODE, NO HC.
  if (isCoaxAsbuilt(r)) {
    const totalAsb = asbA + asbU;

    if (mode === "ADV") {
      const asbuiltBase = totalAsb > 0 ? r2(8.5) : 0;
      const asbuiltGt2000 = totalAsb > 2000
        ? r2(((totalAsb - 2000) / 5280) * 10)
        : 0;
      return r2(asbuiltBase + asbuiltGt2000);
    }

    const asbuiltBase = totalAsb > 0 ? r2(25) : 0;
    const asbuiltGt2000 = totalAsb > 2000
      ? r2((totalAsb - 2000) * 0.014)
      : 0;
    return r2(asbuiltBase + asbuiltGt2000);
  }

  // Fiber Design BAU - reglas especiales:
  // MAIN: 55.75 + Route(Main) + LODE/Nodes*14.25 + HC/Amps*16.25 + Coax>2000*0.019
  // MAIN-SFD: 12.50 + Route(Main-SFD) + LODE*5 + HC*5 + $10 por milla de Coax sobre 2000'
  if (isFiberDesignBAU(r)) {
    const totalRoute = routeA + routeU;
    const totalCoax = coaxA + coaxU;
    const coaxGt2000Component = totalCoax > 2000
      ? (mode === "SFD"
          ? r2((totalCoax - 2000) * 0.019)
          : r2(((totalCoax - 2000) / 5280) * 10))
      : 0;
    let routeComponent = 0;

    if (mode === "SFD") {
      if (totalRoute > 20000) {
        routeComponent = r2(totalRoute * 0.008);
      } else {
        if (totalRoute > 0) routeComponent += r2(30.25);
        if (totalRoute > 2000) routeComponent += r2((totalRoute - 2000) * 0.008);
      }

      return r2(
        55.75 +
        routeComponent +
        (lode * 14.25) +
        (hc * 16.25) +
        coaxGt2000Component
      );
    }

    // ADV / Main-SFD
    if (totalRoute > 20000) {
      routeComponent = r2(totalRoute * 0.008);
    } else {
      if (totalRoute > 0) routeComponent += r2(10);
      if (totalRoute > 2000) {
        routeComponent += r2(((totalRoute - 2000) / 5280) * 12.5);
      }
    }

    return r2(
      12.5 +
      routeComponent +
      (lode * 5) +
      (hc * 5) +
      coaxGt2000Component
    );
  }

  // Fiber Asbuilt BAU / Fiber Bentley Asb - regla vigente:
  // MAIN: 25 + HC/Amps*16.25 + Asbuilt>2000 * 0.014
  // MAIN-SFD: 15 + HC/Amps*5 + $10 por milla de Asbuilt sobre 2000'
  // NO considera Route, Coax ni LODE.
  if (isFiberAsbuiltBAU(r)) {
    const totalAsb = asbA + asbU;

    if (mode === "SFD") {
      const asbuiltGt2000 = totalAsb > 2000
        ? r2((totalAsb - 2000) * 0.014)
        : 0;
      return r2(
        25 +
        (hc * 16.25) +
        asbuiltGt2000
      );
    }

    const asbuiltGt2000 = totalAsb > 2000
      ? r2(((totalAsb - 2000) / 5280) * 10)
      : 0;
    return r2(
      15 +
      (hc * 5) +
      asbuiltGt2000
    );
  }

  // Bentley Split Coax Asbuilt: Main = 105; Main-SFD usa la misma lógica de Iris-Splits.
  if (isBentleySplitCoaxAsbuilt(r)) {
    if (mode === "ADV") return calcBentleyBase(r, "BENTLEY_SFD");
    return r2(105);
  }

  // Bentley Split Fiber Asbuilt:
  // Main = 105 fijo.
  // Main-SFD = regla de Fiber Iris SOLO para Main-SFD.
  if (isBentleySplitFiberAsbuilt(r)) {
    if (mode === "ADV") {
      return r2(30 + Math.max(0, lode - 1) * 15);
    }
    return r2(105);
  }

  if (jobType === "fiberiris" || jobType === "irissplitfiberdesign" || jobType === "irissplitfiberasbuilt" || jobType === "bentleysplitfiberdesign") {
    if (lode <= 0) return 0;
    if (mode === "ADV") return r2(30 + Math.max(0, lode - 1) * 15);
    return r2(60 + Math.max(0, lode - 1) * 33);
  }

  const totalRoute = routeA + routeU;
  const totalCoax = coaxA + coaxU;
  const totalAsb = asbA + asbU;

  let totalStrand = 0;      // AC
  let strandGt2000 = 0;     // AD
  let strandGt20000 = 0;    // AE
  let totalDesign = 0;      // AF
  let designGt2000 = 0;     // AG
  let lodeBase = 0;         // AH
  let lodeGt2000 = 0;       // AI
  let asbuiltBase = 0;      // AJ
  let asbuiltGt2000 = 0;    // AK

  if (mode === "ADV") {
    if (totalRoute > 0) totalStrand = r2(10);
    if (totalCoax > 0) totalDesign = r2(12.5);
    if (totalAsb > 0) asbuiltBase = r2(8.5);
    if (lode > 0 && usesGenericLodeBase(r)) lodeBase = r2(8.5);

    if (totalRoute > 2000) strandGt2000 = r2(((totalRoute - 2000) / 5280) * 12.5);

    if (totalRoute > 20000) {
      totalStrand = 0;
      strandGt2000 = 0;
      strandGt20000 = r2(totalRoute * 0.008);
    }

    if (totalCoax > 2000) designGt2000 = r2(((totalCoax - 2000) / 5280) * 15.5);
    if (totalAsb > 2000) asbuiltGt2000 = r2(((totalAsb - 2000) / 5280) * 10);
    lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.
  } else {
    totalStrand = totalRoute > 0 ? r2(30.25) : 0;
    strandGt2000 = totalRoute > 2000 ? r2((totalRoute - 2000) * 0.008) : 0;
    strandGt20000 = 0;

    if (totalRoute > 20000) {
      totalStrand = 0;
      strandGt2000 = 0;
      strandGt20000 = r2(totalRoute * 0.008);
    }

    totalDesign = totalCoax > 0 ? r2(36.25) : 0;
    designGt2000 = totalCoax > 2000 ? r2((totalCoax - 2000) * 0.014) : 0;

    lodeBase = (lode > 0 && usesGenericLodeBase(r)) ? r2(15) : 0;
    lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.

    asbuiltBase = totalAsb > 0 ? r2(25) : 0;
    asbuiltGt2000 = totalAsb > 2000 ? r2((totalAsb - 2000) * 0.014) : 0;
  }

  return r2(
    totalStrand +
      strandGt2000 +
      strandGt20000 +
      totalDesign +
      designGt2000 +
      lodeBase +
      lodeGt2000 +
      asbuiltBase +
      asbuiltGt2000
  );
}

// Coax Bentley: devuelve TOTAL (columna AC/TOTAL) según modo
function calcBentleyBase(r, mode /* "SFD" | "BENTLEY_SFD" */) {
  const lode = n(r?.joblode);

  if (mode === "BENTLEY_SFD") {
    if (isProject01(r)) return 0;

    const project = String(r?.jobpro ?? "");
    const rawW = r?.joblode;
    const rawX = r?.jobhc;

    const isBlankW = rawW === "" || rawW === null || rawW === undefined;
    if (isBlankW) return 0;

    const W = parseFloat(String(rawW).replace(",", "."));
    const Xs = String(rawX ?? "");

    const isXEmptyOrZeroOrDash = Xs === "" || Xs === "-" || Xs === "0" || Xs === "0.0";

    const X = parseFloat(String(rawX ?? "").replace(",", "."));
    const xNum = isNaN(X) ? 0 : X;

    const calcFromX = () => {
      if (isXEmptyOrZeroOrDash) return 15;
      if (xNum <= 14) return 30;
      if (xNum <= 24) return 45;
      return 60;
    };

    if (project === "Project-02" || project === "Project-03") {
      if (W === 2) return 60;
      if (W === 1) return calcFromX();
      return 0;
    }

    if (W >= 1) return calcFromX();
    return 0;
  }

  // SFD (viejo)
  if (lode === 0) return 0;
  if (lode === 1) return 125 + 55;
  if (lode > 1) return (125 * lode) + 55;
  return 0;
}

export function billingRowTotal(row, sfd = false) {
  const type = normalize(row?.jobti);
  const bentley = ["coaxbentley", "irissplits", "irissplitcoaxdesign", "bentleysplitcoaxdesign"].includes(type);
  return bentley ? calcBentleyTotal(row, sfd ? "BENTLEY_SFD" : "SFD") : calcMainAL(row, sfd ? "ADV" : "SFD");
}
const billingDetailLabels = {
  __extra_client: "Total x H extras (Main / Coax Bentley)",
  __total_client: "TOTAL (Main / Coax Bentley)",
  __extra_sfd: "Total x H extras (SFD)",
  __total_sfd: "TOTAL (SFD)",
};
function billingDetailValue(row, column) {
  if (column === "jbhrex") return getExtraHours(row).toFixed(2);
  if (column === "__extra_client") return calcExtraHoursTotal(row, "CLIENT").toFixed(2);
  if (column === "__extra_sfd") return calcExtraHoursTotal(row, "SFD").toFixed(2);
  if (column === "__total_client") return billingRowTotal(row).toFixed(2);
  if (column === "__total_sfd") return billingRowTotal(row, true).toFixed(2);
  return String(row?.[column] ?? "");
}

/* ================= Export XLS (independiente de GridPage) ================= */
/* ================= Export XLS (independiente de GridPage) ================= */
function exportAdminXLS(records, sheetFlags) {
  const flags = {
    main: !!sheetFlags?.main,
    bentley: !!sheetFlags?.bentley,
    mainSFD: !!sheetFlags?.mainSFD,
    bentleySFD: !!sheetFlags?.bentleySFD,
  };

  const rowsAll = filterOutExcludedXLS(records).slice().sort((a, b) =>
    String(a?.jobti || "").localeCompare(String(b?.jobti || ""), undefined, { sensitivity: "base" })
  );

  const isCoaxBentley = (r) => {
    const t = normalize(r?.jobti);
    return t === "coaxbentley" || t === "irissplits" || t === "irissplitcoaxdesign" || t === "bentleysplitcoaxdesign";
  };
  const rowsBentley = rowsAll.filter((r) => isCoaxBentley(r));
  const rowsMain = rowsAll.filter((r) => !isCoaxBentley(r));

  const headersBase = [
    "#",
    "Fav",
    "Job Id",
    "Status",
    "User",
    "Type",
    "EP Status",
    "EP Closed Date",
    "Project",
    "Submission Date",
    "Notes Admin",
    "Notes",
    "OU-Comcast",
    "Route Aerial",
    "Route UG",
    "Total Route",
    "Coax AE",
    "Coax UG",
    "Total Coax",
    "Asb AE",
    "Asb UG",
    "Total Asb",
    "LODE",
    "HC",
    "Hr Extras",
    "Project Name",
    "Project Port",
    "COM Job Type",
    "LOB",
  ];

  const headersBentley = [...headersBase, "Total x H extras", "TOTAL"];

  const headersMain = [
    ...headersBase,
    "Total Strand",
    "Total Strand > 2000",
    "Total Strand > 20000",
    "Total Design",
    "Total Design > 2000",
    "Lode",
    "Lode > 2000",
    "Asbuilt",
    "Asbuilt > 2000",
    "Total x H extras",
    "TOTAL",
  ];

  const escapeXml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");

  const cell = (v, type = "String", styleId = "") =>
    styleId
      ? `<Cell ss:StyleID="${styleId}"><Data ss:Type="${type}">${escapeXml(v)}</Data></Cell>`
      : `<Cell><Data ss:Type="${type}">${escapeXml(v)}</Data></Cell>`;

  const rowXml = (vals, types = [], styles = []) =>
    `<Row ss:AutoFitHeight="0" ss:Height="15">${vals
      .map((v, idx) => cell(v, types[idx] || "String", styles[idx] || ""))
      .join("")}</Row>`;

  const buildSummaryTable = (flags, sumMain, sumBentley, sumMainSFD, sumBentleySFD, totalFinal) => {
    const headers = ["Concepto", "Total"];
    const headerRow = rowXml(headers, headers.map(() => "String"), headers.map(() => "hdr"));

    const rows = [];
    if (flags?.main) rows.push(["Main", sumMain.toFixed(2)]);
    if (flags?.bentley) rows.push(["Coax Bentley", sumBentley.toFixed(2)]);
    if (flags?.mainSFD) rows.push(["Main-SFD", sumMainSFD.toFixed(2)]);
    if (flags?.bentleySFD) rows.push(["Coax Bentley SFD", sumBentleySFD.toFixed(2)]);
    rows.push(["TOTAL FINAL", totalFinal.toFixed(2)]);

    const columnsXml =
      `<Column ss:AutoFitWidth="0" ss:Width="180"/>` +
      `<Column ss:AutoFitWidth="0" ss:Width="110"/>`;

    const bodyRows = rows
      .map((r, idx) => {
        const isAlt = idx % 2 === 1;
        const s = isAlt ? "cellAlt" : "cell";
        const styles = [s, isAlt ? "twoDecAlt" : "twoDec"];
        const types = ["String", "Number"];

        if (r[0] === "TOTAL FINAL") {
          styles[0] = "hdr";
          styles[1] = "twoDec";
        }
        return rowXml(r, types, styles);
      })
      .join("");

    return `<Table ss:DefaultRowHeight="15">${columnsXml}${headerRow}${bodyRows}</Table>`;
  };

  const buildTable = (rows, headers, isMainSheet, mode) => {
    const headerRow = rowXml(headers, headers.map(() => "String"), headers.map(() => "hdr"));

    let sumLast = 0;

    const bodyRows = rows
      .map((r, idx) => {
        const routeA = n(r?.jobrouteaerial);
        const routeU = n(r?.jobrouteug);
        const coaxA = n(r?.jobcoaxae);
        const coaxU = n(r?.jobcoaxug);
        const asbA = n(r?.jobasbae);
        const asbU = n(r?.jobasbug);
        const lode = n(r?.joblode);
        const hc = n(r?.jobhc);

        const totalRoute = routeA + routeU;
        const totalCoax = coaxA + coaxU;
        const totalAsb = asbA + asbU;

        if (isMainSheet) {
          // AL final
          const al = calcMainAL(r, mode);

          let totalStrand = 0;
          let strandGt2000 = 0;
          let strandGt20000 = 0;
          let totalDesign = 0;
          let designGt2000 = 0;
          let lodeBase = 0;
          let lodeGt2000 = 0;
          let asbuiltBase = 0;
          let asbuiltGt2000 = 0;

          if (mode === "ADV" && isProject01(r)) {
            // Main-SFD: Project-01 debe quedar en 0.
          } else if (isRouteOnlyType(r)) {
            // Route / Route FTTP: SOLO Route.
            // NO Coax, NO Asbuilt, NO LODE, NO HC.
            if (mode === "ADV") {
              if (totalRoute > 20000) {
                strandGt20000 = r2(totalRoute * 0.008);
              } else {
                totalStrand = totalRoute > 0 ? r2(10) : 0;
                strandGt2000 = totalRoute > 2000
                  ? r2(((totalRoute - 2000) / 5280) * 12.5)
                  : 0;
              }
            } else {
              if (totalRoute > 20000) {
                strandGt20000 = r2(totalRoute * 0.008);
              } else {
                totalStrand = totalRoute > 0 ? r2(30.25) : 0;
                strandGt2000 = totalRoute > 2000
                  ? r2((totalRoute - 2000) * 0.008)
                  : 0;
              }
            }
          } else if (isCoaxAsbuilt(r)) {
            // Coax Asbuilt / Coax Bentley Asb: SOLO Total Asbuilt.
            // NO Route, NO Coax, NO LODE, NO HC.
            if (mode === "ADV") {
              asbuiltBase = totalAsb > 0 ? r2(8.5) : 0;
              asbuiltGt2000 = totalAsb > 2000
                ? r2(((totalAsb - 2000) / 5280) * 10)
                : 0;
            } else {
              asbuiltBase = totalAsb > 0 ? r2(25) : 0;
              asbuiltGt2000 = totalAsb > 2000
                ? r2((totalAsb - 2000) * 0.014)
                : 0;
            }
          } else if (isFiberDesignBAU(r)) {
            if (mode === "SFD") {
              // Fiber Design BAU MAIN:
              // 55.75 + Route(Main) + Nodes/LODE*14.25 + HC/Amps*16.25
              if (totalRoute > 20000) {
                strandGt20000 = r2(totalRoute * 0.008);
              } else {
                totalStrand = totalRoute > 0 ? r2(30.25) : 0;
                strandGt2000 = totalRoute > 2000 ? r2((totalRoute - 2000) * 0.008) : 0;
              }
              totalDesign = r2(55.75);
              designGt2000 = totalCoax > 2000
                ? r2((totalCoax - 2000) * 0.019)
                : 0;
              lodeBase = r2(lode * 14.25);
              asbuiltBase = r2(hc * 16.25);
            } else {
              // Fiber Design BAU MAIN-SFD:
              // 12.50 + Route(Main-SFD) + LODE*5 + HC*5
              if (totalRoute > 20000) {
                strandGt20000 = r2(totalRoute * 0.008);
              } else {
                totalStrand = totalRoute > 0 ? r2(10) : 0;
                strandGt2000 = totalRoute > 2000
                  ? r2(((totalRoute - 2000) / 5280) * 12.5)
                  : 0;
              }
              totalDesign = r2(12.5);
              designGt2000 = totalCoax > 2000
                ? r2(((totalCoax - 2000) / 5280) * 10)
                : 0;
              lodeBase = r2(lode * 5);
              asbuiltBase = r2(hc * 5);
            }
          } else if (isFiberAsbuiltBAU(r)) {
            if (mode === "SFD") {
              // Fiber Asbuilt BAU / Fiber Bentley Asb MAIN:
              // 25 + HC/Amps*16.25 + Asbuilt>2000*0.014. NO Route, NO Coax, NO LODE.
              totalDesign = r2(25);
              lodeBase = 0;
              asbuiltBase = r2(hc * 16.25);
              asbuiltGt2000 = totalAsb > 2000
                ? r2((totalAsb - 2000) * 0.014)
                : 0;
            } else {
              // Fiber Asbuilt BAU / Fiber Bentley Asb MAIN-SFD:
              // 15 + HC/Amps*5 + $10 por milla de Asbuilt sobre 2000'. NO Route, NO Coax, NO LODE.
              totalDesign = r2(15);
              lodeBase = 0;
              asbuiltBase = r2(hc * 5);
              asbuiltGt2000 = totalAsb > 2000
                ? r2(((totalAsb - 2000) / 5280) * 10)
                : 0;
            }
          } else if (isBentleySplitCoaxAsbuilt(r)) {
            if (mode === "ADV") {
              // Misma lógica de Iris-Splits en Main-SFD.
              lodeBase = calcBentleyTotal(r, "BENTLEY_SFD");
            } else {
              // Main: total fijo solicitado para este Type.
              asbuiltBase = r2(105);
            }
          } else if (isBentleySplitFiberAsbuilt(r)) {
            if (mode === "ADV") {
              // Main-SFD: base 30 incluso con LODE=0; luego +15 por cada LODE adicional sobre 1.
              lodeBase = r2(30 + Math.max(0, lode - 1) * 15);
            } else {
              // Main: total fijo.
              asbuiltBase = r2(105);
            }
          } else if (["fiberiris", "irissplitfiberdesign", "irissplitfiberasbuilt", "bentleysplitfiberdesign"].includes(normalizeCalcJobType(r?.jobti))) {
            if (mode === "ADV") {
              lodeBase = lode > 0 ? r2(30 + Math.max(0, lode - 1) * 15) : 0;
            } else {
              lodeBase = lode > 0 ? r2(60 + Math.max(0, lode - 1) * 33) : 0;
            }
          } else if (mode === "ADV") {
            if (totalRoute > 0) totalStrand = r2(10);
            if (totalCoax > 0) totalDesign = r2(12.5);
            if (totalAsb > 0) asbuiltBase = r2(8.5);
            if (lode > 0 && usesGenericLodeBase(r)) lodeBase = r2(8.5);

            if (totalRoute > 2000) strandGt2000 = r2(((totalRoute - 2000) / 5280) * 12.5);

            if (totalRoute > 20000) {
              totalStrand = 0;
              strandGt2000 = 0;
              strandGt20000 = r2(totalRoute * 0.008);
            }

            if (totalCoax > 2000) designGt2000 = r2(((totalCoax - 2000) / 5280) * 15.5);
            if (totalAsb > 2000) asbuiltGt2000 = r2(((totalAsb - 2000) / 5280) * 10);
            lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.
          } else {
            totalStrand = totalRoute > 0 ? r2(30.25) : 0;
            strandGt2000 = totalRoute > 2000 ? r2((totalRoute - 2000) * 0.008) : 0;
            strandGt20000 = 0;

            if (totalRoute > 20000) {
              totalStrand = 0;
              strandGt2000 = 0;
              strandGt20000 = r2(totalRoute * 0.008);
            }

            totalDesign = totalCoax > 0 ? r2(36.25) : 0;
            designGt2000 = totalCoax > 2000 ? r2((totalCoax - 2000) * 0.014) : 0;

            lodeBase = (lode > 0 && usesGenericLodeBase(r)) ? r2(15) : 0;
            lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.

            asbuiltBase = totalAsb > 0 ? r2(25) : 0;
            asbuiltGt2000 = totalAsb > 2000 ? r2((totalAsb - 2000) * 0.014) : 0;
          }

          if (isCoaxAsbRevertSplit(r)) {
            // Mantener las columnas de detalle alineadas con AL para el XLS exportado.
            totalStrand = 0;
            strandGt2000 = 0;
            strandGt20000 = 0;
            totalDesign = 0;
            designGt2000 = 0;
            lodeBase = 0;
            lodeGt2000 = 0;
            if (mode === "ADV") {
              asbuiltBase = r2(12.5);
              asbuiltGt2000 = r2(Math.max(0, hc - 20) * 0.5);
            } else {
              asbuiltBase = r2(160);
              asbuiltGt2000 = 0;
            }
          }

          if (isIrisSplitCoaxAsbuilt(r)) {
            // Iris Split Coax Asbuilt: Main 105 fijo; Main-SFD usa la regla de Coax Asb Revert Split.
            totalStrand = 0;
            strandGt2000 = 0;
            strandGt20000 = 0;
            totalDesign = 0;
            designGt2000 = 0;
            lodeBase = 0;
            lodeGt2000 = 0;
            if (mode === "ADV") {
              asbuiltBase = r2(12.5);
              asbuiltGt2000 = r2(Math.max(0, hc - 20) * 0.5);
            } else {
              asbuiltBase = r2(105);
              asbuiltGt2000 = 0;
            }
          }

          if (isCoaxAsbRevertN0(r)) {
            // Coax Asb Revert N+0: total fijo. No sumar Route, Coax, LODE, Asb ni HC.
            totalStrand = 0;
            strandGt2000 = 0;
            strandGt20000 = 0;
            totalDesign = 0;
            designGt2000 = 0;
            lodeBase = 0;
            lodeGt2000 = 0;
            asbuiltBase = mode === "ADV" ? r2(10) : r2(20);
            asbuiltGt2000 = 0;
          }

          const valsBase = [
            idx + 1,
            "",
            r?.jobid ?? "",
            r?.jobsta ?? "",
            r?.jobuser ?? "",
            r?.jobti ?? "",
            r?.jobclo ?? "",
            formatDateTimeHM(r?.jobclodate || ""),
            r?.jobpro ?? "",
            r?.jobdatesub ?? "",
            r?.jobnotesadmin ?? "",
            r?.jobnotes ?? "",
            r?.jobou ?? "",
            r?.jobrouteaerial ?? "",
            r?.jobrouteug ?? "",
            totalRoute,
            r?.jobcoaxae ?? "",
            r?.jobcoaxug ?? "",
            totalCoax,
            r?.jobasbae ?? "",
            r?.jobasbug ?? "",
            totalAsb,
            r?.joblode ?? "",
            r?.jobhc ?? "",
            getExtraHours(r),
            r?.jobproname || "",
            r?.jobproport || "",
            r?.jobcomty || "",
            r?.joblob || "",
          ];

          const vals = [
            ...valsBase,
            totalStrand,
            strandGt2000,
            strandGt20000,
            totalDesign,
            designGt2000,
            lodeBase,
            lodeGt2000,
            asbuiltBase,
            asbuiltGt2000,
            calcExtraHoursTotal(r, mode === "ADV" ? "SFD" : "CLIENT"),
            al,
          ];

          sumLast += al;

          const types = vals.map(() => "String");
          types[0] = "Number";
          types[15] = "Number";
          types[18] = "Number";
          types[21] = "Number";
          types[24] = "Number";
          const isAlt = idx % 2 === 1;
          const baseStyle = isAlt ? "cellAlt" : "cell";
          const styles = vals.map(() => baseStyle);

          styles[15] = isAlt ? "intAlt" : "int";
          styles[18] = isAlt ? "intAlt" : "int";
          styles[21] = isAlt ? "intAlt" : "int";
          styles[24] = isAlt ? "twoDecAlt" : "twoDec";

          const baseLen = headersBase.length;
          for (let k = baseLen; k < headers.length; k++) {
            types[k] = "Number";
            styles[k] = isAlt ? "twoDecAlt" : "twoDec";
          }

          return rowXml(vals, types, styles);
        } else {
          const total = calcBentleyTotal(r, mode);

          const valsBase = [
            idx + 1,
            "",
            r?.jobid ?? "",
            r?.jobsta ?? "",
            r?.jobuser ?? "",
            r?.jobti ?? "",
            r?.jobclo ?? "",
            formatDateTimeHM(r?.jobclodate || ""),
            r?.jobpro ?? "",
            r?.jobdatesub ?? "",
            r?.jobnotesadmin ?? "",
            r?.jobnotes ?? "",
            r?.jobou ?? "",
            r?.jobrouteaerial ?? "",
            r?.jobrouteug ?? "",
            totalRoute,
            r?.jobcoaxae ?? "",
            r?.jobcoaxug ?? "",
            totalCoax,
            r?.jobasbae ?? "",
            r?.jobasbug ?? "",
            totalAsb,
            r?.joblode ?? "",
            r?.jobhc ?? "",
            getExtraHours(r),
            r?.jobproname || "",
            r?.jobproport || "",
            r?.jobcomty || "",
            r?.joblob || "",
          ];

          const vals = [...valsBase, calcExtraHoursTotal(r, mode === "BENTLEY_SFD" ? "SFD" : "CLIENT"), total];
          sumLast += Number(total) || 0;

          const types = vals.map(() => "String");
          types[0] = "Number";
          types[15] = "Number";
          types[18] = "Number";
          types[21] = "Number";
          types[24] = "Number";
          types[vals.length - 1] = "Number";
          types[vals.length - 2] = "Number";

          const isAlt = idx % 2 === 1;
          const baseStyle = isAlt ? "cellAlt" : "cell";
          const styles = vals.map(() => baseStyle);

          styles[15] = isAlt ? "intAlt" : "int";
          styles[18] = isAlt ? "intAlt" : "int";
          styles[21] = isAlt ? "intAlt" : "int";
          styles[24] = isAlt ? "twoDecAlt" : "twoDec";
          styles[vals.length - 1] = isAlt ? "twoDecAlt" : "twoDec";
          styles[vals.length - 2] = isAlt ? "twoDecAlt" : "twoDec";

          return rowXml(vals, types, styles);
        }
      })
      .join("");

    const totalVals = headers.map(() => "");
    totalVals[Math.max(0, headers.length - 2)] = "TOTAL";
    totalVals[headers.length - 1] = r2(sumLast);

    totalVals[headers.length - 3] = "TOTAL";
    totalVals[headers.length - 2] = r2(rows.reduce((sum, row) => sum + calcExtraHoursTotal(row, (mode === "ADV" || mode === "BENTLEY_SFD") ? "SFD" : "CLIENT"), 0));
    const totalTypes = headers.map(() => "String");
    totalTypes[headers.length - 1] = "Number";
    totalTypes[headers.length - 2] = "Number";

    const totalStyles = headers.map(() => "hdr");
    totalStyles[headers.length - 1] = "twoDec";
    totalStyles[headers.length - 2] = "twoDec";

    const footerRow = rowXml(totalVals, totalTypes, totalStyles);

    const colWidth = (h) => {
      const key = String(h || "").toLowerCase();
      if (key === "#") return 28;
      if (key === "fav") return 35;
      if (key === "job id") return 110;
      if (key === "status") return 95;
      if (key === "user") return 95;
      if (key === "type") return 105;
      if (key === "ep status") return 95;
      if (key === "ep closed date") return 135;
      if (key === "project") return 90;
      if (key === "submission date") return 135;
      if (key === "notes admin") return 120;
      if (key === "notes") return 120;
      if (key === "ou-comcast") return 105;
      if (key.includes("route")) return 85;
      if (key.includes("coax")) return 85;
      if (key.includes("asb")) return 85;
      if (key.includes("total")) return 85;
      if (key === "lode") return 70;
      if (key === "hc") return 55;
      if (key === "project name") return 140;
      if (key === "project port") return 95;
      if (key === "com job type") return 120;
      if (key === "lob") return 70;
      if (key === "al") return 85;
      return 95;
    };

    const columnsXml = headers
      .map((h) => `<Column ss:AutoFitWidth="0" ss:Width="${colWidth(h)}"/>`)
      .join("");

    return {
      tableXml: `<Table ss:DefaultRowHeight="15">${columnsXml}${headerRow}${bodyRows}${footerRow}</Table>`,
      sumLast: r2(sumLast),
    };
  };

  // Totales para Resumen (solo si la hoja está marcada)
  const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
  const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
  const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
  const sumBentleySFD = flags.bentleySFD ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0)) : 0;

  const baseTotal = r2(sumMain + sumBentley);
  const sfdTotal = r2(sumMainSFD + sumBentleySFD);
  const hasBaseSelection = !!(flags.main || flags.bentley);
  // Si Main/Coax Bentley NO están seleccionados, el TOTAL FINAL es la suma de las hojas seleccionadas.
  // Si Main+Coax Bentley es 0, NO hacemos la diferencia con SFD (evita negativos cuando no hay data base)
  const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

  const sheets = [];
  sheets.push(
    `<Worksheet ss:Name="Resumen">${buildSummaryTable(flags, sumMain, sumBentley, sumMainSFD, sumBentleySFD, totalFinal)}</Worksheet>`
  );

  if (flags.main) {
    sheets.push(`<Worksheet ss:Name="Main">${buildTable(rowsMain, headersMain, true, "SFD").tableXml}</Worksheet>`);
  }
  if (flags.mainSFD) {
    sheets.push(`<Worksheet ss:Name="Main-SFD">${buildTable(rowsMain, headersMain, true, "ADV").tableXml}</Worksheet>`);
  }
  if (flags.bentley) {
    sheets.push(`<Worksheet ss:Name="Coax Bentley">${buildTable(rowsBentley, headersBentley, false, "SFD").tableXml}</Worksheet>`);
  }
  if (flags.bentleySFD) {
    sheets.push(`<Worksheet ss:Name="Coax Bentley SFD">${buildTable(rowsBentley, headersBentley, false, "BENTLEY_SFD").tableXml}</Worksheet>`);
  }

  const workbook =
    `<?xml version="1.0"?>
` +
    `<?mso-application progid="Excel.Sheet"?>
` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
` +
    ` xmlns:o="urn:schemas-microsoft-com:office:office"
` +
    ` xmlns:x="urn:schemas-microsoft-com:office:excel"
` +
    ` xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
` +
    ` xmlns:html="http://www.w3.org/TR/REC-html40">
` +
    `<Styles>
` +
    `  <Style ss:ID="hdr">
` +
    `    <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
` +
    `    <Font ss:Bold="1" ss:Color="#FFFFFF"/>
` +
    `    <Interior ss:Color="#8B5A2B" ss:Pattern="Solid"/>
` +
    `    <Borders>
` +
    `      <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#6E4A26"/>
` +
    `      <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#6E4A26"/>
` +
    `      <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#6E4A26"/>
` +
    `      <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#6E4A26"/>
` +
    `    </Borders>
` +
    `  </Style>
` +
    `  <Style ss:ID="cell">
` +
    `    <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
` +
    `    <Font ss:Color="#000000"/>
` +
    `    <Interior ss:Color="#FFF7E6" ss:Pattern="Solid"/>
` +
    `    <Borders>
` +
    `      <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `      <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `      <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `      <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `    </Borders>
` +
    `  </Style>
` +
    `  <Style ss:ID="cellAlt">
` +
    `    <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
` +
    `    <Font ss:Color="#000000"/>
` +
    `    <Interior ss:Color="#FFF1D6" ss:Pattern="Solid"/>
` +
    `    <Borders>
` +
    `      <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `      <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `      <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `      <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D0C7B8"/>
` +
    `    </Borders>
` +
    `  </Style>
` +
    `  <Style ss:ID="twoDec" ss:Parent="cell"><NumberFormat ss:Format="0.00"/></Style>
` +
    `  <Style ss:ID="twoDecAlt" ss:Parent="cellAlt"><NumberFormat ss:Format="0.00"/></Style>
` +
    `  <Style ss:ID="int" ss:Parent="cell"><NumberFormat ss:Format="0"/></Style>
` +
    `  <Style ss:ID="intAlt" ss:Parent="cellAlt"><NumberFormat ss:Format="0"/></Style>
` +
    `</Styles>
` +
    sheets.join("\n") +
    `
</Workbook>`;

  const blob = new Blob([workbook], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  const name = `projects_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(
    ts.getHours()
  )}${pad(ts.getMinutes())}.xls`;
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Reportes() {
  const options = [
    "All",
    "Project-01",
    "Project-02",
    "Project-03",
    "Project-04",
    "Project-05",
    "Project-06",
    "Project-07",
    "Project-08",
    "Admin",
  ];

  const [selected, setSelected] = useState(options[0]);
  const [downloading, setDownloading] = useState(false);

  // Status dropdown (solo para Admin)
  const [statusOptions] = useState(FIXED_STATUS_OPTIONS);
  const [selectedStatus, setSelectedStatus] = useState("All");

  const [chkMain, setChkMain] = useState(true);
  const [chkBentley, setChkBentley] = useState(true);
  const [chkMainSFD, setChkMainSFD] = useState(true);
  const [chkBentleySFD, setChkBentleySFD] = useState(true);

  const [summaryUnlocked, setSummaryUnlocked] = useState(false);

  // Fecha a procesar para el cuadro "Por Project"
  const [processDate, setProcessDate] = useState(() => getDateKey(new Date()));

  // ================= Grid Projects (solo lectura) =================
  const [projectsRows, setProjectsRows] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsErr, setProjectsErr] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [actionErr, setActionErr] = useState("");

  const getRowId = (r, idx) => {
    const id = r?.jobid ?? r?.id ?? r?.job_id ?? "";
    return id !== "" && id !== null && id !== undefined ? String(id) : `row_${idx}`;
  };

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setProjectsLoading(true);
      setProjectsErr("");
      try {
        const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
        if (!alive) return;

        if (data?.status === "success") {
          const arr = Array.isArray(data.data) ? data.data : [];
          setProjectsRows(arr);
          setSelectedRowIds(new Set());
        } else {
          setProjectsErr("No se pudo cargar Projects.");
        }
      } catch (e) {
        if (!alive) return;
        setProjectsErr("Error de conexión al cargar Projects.");
      } finally {
        if (!alive) return;
        setProjectsLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);

  const columns = useMemo(() => {
    if (!projectsRows || projectsRows.length === 0) return [];

    const preferred = [
      "jobid",
      "jobsta",
      "jobuser",
      "jobti",
      "jobclo",
      "jobclodate",
      "jobpro",
      "jobdatesub",
      "jobnotesadmin",
      "jobnotes",
      "jobou",
      "jobrouteaerial",
      "jobrouteug",
      "jobcoaxae",
      "jobcoaxug",
      "jobasbae",
      "jobasbug",
      "joblode",
      "jobhc",
      "jbhrex",
      "jobproname",
      "jobproport",
      "jobcomty",
      "joblob",
    ];

    const allKeys = new Set();
    projectsRows.forEach((r) => Object.keys(r || {}).forEach((k) => allKeys.add(k)));

    const ordered = [];
    preferred.forEach((k) => {
      if (allKeys.has(k)) ordered.push(k);
      allKeys.delete(k);
    });

    Array.from(allKeys)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .forEach((k) => ordered.push(k));

    return [...ordered, ...Object.keys(billingDetailLabels)];
  }, [projectsRows]);

  const allRowIds = useMemo(
    () => projectsRows.map((r, idx) => getRowId(r, idx)),
    [projectsRows]
  );


  const readyForQcRowIds = useMemo(() => {
    const ids = [];
    projectsRows.forEach((r, idx) => {
      const st = String(r?.jobsta ?? "").trim().toLowerCase();
      if (st === "ready for qc") ids.push(getRowId(r, idx));
    });
    return ids;
  }, [projectsRows]);

  const isReadyForQcSelected =
    readyForQcRowIds.length > 0 &&
    readyForQcRowIds.every((id) => selectedRowIds.has(id));

  const toggleReadyForQc = (e) => {
    const checked = e?.target?.checked;
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (checked) readyForQcRowIds.forEach((id) => next.add(id));
      else readyForQcRowIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const isAllSelected =
    allRowIds.length > 0 && selectedRowIds.size === allRowIds.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedRowIds(new Set());
    else setSelectedRowIds(new Set(allRowIds));
  };

  const toggleRow = (rowId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

// Acción: pasar SOLO los seleccionados a Billing (actualiza en backend)
// Acción: pasar SOLO los seleccionados a Billing (actualiza en backend)

const applyBillingToSelected = async () => {
  if (selectedRowIds.size === 0 || actionLoading) return;

  setActionErr("");
  const ids = Array.from(selectedRowIds);

  // Confirmación explícita para evitar errores en base activa
  const confirm = window.prompt(
    `Vas a pasar a Billing ${ids.length} registro(s).\n\nEscribe BILLING para confirmar:`
  );
  if (confirm !== "BILLING") return;

  setActionLoading(true);

  // Mapa "antes" para comparar luego
  const before = new Map();
  projectsRows.forEach((r, idx) => {
    const rid = getRowId(r, idx);
    if (ids.includes(rid)) before.set(rid, String(r?.jobsta ?? ""));
  });

  const parseMaybeJson = async (resp) => {
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    const raw = await resp.text();
    let json = null;
    if (ct.includes("application/json")) {
      try { json = JSON.parse(raw); } catch {}
    } else {
      // a veces el backend devuelve JSON sin content-type correcto
      try { json = JSON.parse(raw); } catch {}
    }
    return { raw, json };
  };

  const looksSuccess = (obj, raw) => {
    if (obj && typeof obj === "object") {
      const st = String(obj.status ?? obj.result ?? "").toLowerCase();
      if (st === "success" || st === "ok") return true;
      if (obj.success === true || obj.ok === true) return true;
      if (typeof obj.affected !== "undefined" && Number(obj.affected) > 0) return true;
      if (typeof obj.updated !== "undefined" && Number(obj.updated) > 0) return true;
    }
    const s = String(raw || "").toLowerCase();
    if (s.includes("success") || s.includes("updated") || s.includes("ok")) return true;
    if (s.trim() === "1") return true;
    return false;
  };

  // Preferimos proxy same-origin si existe, así podemos leer la respuesta (sin CORS).
  const sameOrigin = window.location.origin.replace(/\/+$/, "");
  const endpointCandidates = [
    `${sameOrigin}/api/update_projects.php`,
    `${sameOrigin}/api/update_project.php`,
    `${sameOrigin}/api/update_project_status.php`,
    `${sameOrigin}/update_projects.php`,
    `${sameOrigin}/update_project.php`,
  ];

  // Formatos típicos de backend (campo/valor, columna, etc.)
  const payloadVariants = (jobid) => [
    new URLSearchParams({ jobid, jobsta: "Billing" }),
    new URLSearchParams({ jobid, status: "Billing" }),
    new URLSearchParams({ jobid, jobstatus: "Billing" }),
    new URLSearchParams({ jobid, field: "jobsta", value: "Billing" }),
    new URLSearchParams({ jobid, column: "jobsta", value: "Billing" }),
    new URLSearchParams({ jobid, col: "jobsta", val: "Billing" }),
    new URLSearchParams({ id: jobid, field: "jobsta", value: "Billing" }),
    new URLSearchParams({ job_id: jobid, field: "jobsta", value: "Billing" }),
    new URLSearchParams({ jobid, table: "projects", field: "jobsta", value: "Billing" }),
  ];

  // Registro para mostrar diagnóstico
  let lastDiag = { url: "", method: "", payload: "", status: "", snippet: "" };

  const sendOnce = async (url, method, params) => {
    try {
      const fullUrl =
        method === "GET" ? `${url}${url.includes("?") ? "&" : "?"}${params.toString()}` : url;

      const resp = await fetch(fullUrl, {
        method,
        headers: method === "POST"
          ? { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" }
          : undefined,
        body: method === "POST" ? params.toString() : undefined,
        credentials: "omit",
      });

      const { raw, json } = await parseMaybeJson(resp);
      const snippet = String(raw || "").replace(/\s+/g, " ").slice(0, 180);

      lastDiag = {
        url: fullUrl,
        method,
        payload: params.toString(),
        status: `HTTP ${resp.status}`,
        snippet,
      };

      return { ok: resp.ok && looksSuccess(json, raw) };
    } catch (e) {
      lastDiag = {
        url,
        method,
        payload: params.toString(),
        status: "NETWORK",
        snippet: String(e?.message || "Network error").slice(0, 180),
      };
      return { ok: false };
    }
  };

  const tryUpdateOne = async (jobid) => {
    for (const ep of endpointCandidates) {
      for (const params of payloadVariants(jobid)) {
        const postRes = await sendOnce(ep, "POST", params);
        if (postRes.ok) return true;

        const getRes = await sendOnce(ep, "GET", params);
        if (getRes.ok) return true;
      }
    }
    return false;
  };

  try {
    // 1) actualizar uno por uno SOLO los seleccionados
    for (const id of ids) {
      await tryUpdateOne(id);
    }

    // 2) recargar desde backend (este GET ya funciona en tu entorno)
    const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
    if (data?.status === "success") {
      const arr = Array.isArray(data.data) ? data.data : [];
      setProjectsRows(arr);

      // 3) verificar cambios
      const afterMap = new Map();
      arr.forEach((r, idx) => afterMap.set(getRowId(r, idx), String(r?.jobsta ?? "")));

      let changed = 0;
      ids.forEach((id) => {
        const b = before.get(id);
        const a = afterMap.get(id);
        if (a === "Billing" && b !== "Billing") changed += 1;
      });

      if (changed > 0) setSelectedRowIds(new Set());

      if (changed > 0) {
        setActionErr(`OK: se pasó a Billing ${changed} de ${ids.length} registro(s).`);
      } else {
        const diag = lastDiag?.url
          ? ` Último intento: ${lastDiag.method} ${lastDiag.url} | ${lastDiag.status} | ${lastDiag.snippet}`
          : "";
        setActionErr(
          `No se detectó cambio a Billing en los seleccionados (0 de ${ids.length}). Posible endpoint/param distinto en backend.` +
            diag
        );
      }
    } else {
      setActionErr("No se pudo recargar Projects para verificar el cambio.");
    }
  } catch (e) {
    const diag = lastDiag?.url
      ? ` Último intento: ${lastDiag.method} ${lastDiag.url} | ${lastDiag.status} | ${lastDiag.snippet}`
      : "";
    setActionErr("Error al pasar a Billing (backend no disponible o CORS/proxy)." + diag);
  } finally {
    setActionLoading(false);
  }
};

const applyPayedToSelectedBilling = async () => {
  if (actionLoading) return;

  // Payed afecta a TODOS los registros con jobsta="Billing" (no depende de seleccionados)
  const ids = [];
  const before = new Map();

  projectsRows.forEach((r, idx) => {
    const rid = getRowId(r, idx);
    const st = String(r?.jobsta ?? "").trim();
    if (st === "Billing") {
      ids.push(rid);
      before.set(rid, st);
    }
  });

  if (ids.length === 0) {
    setActionErr('Payed: no se encontraron registros con jobsta="Billing".');
    return;
  }

  setActionErr("");

  // Mensaje de seguridad antes de procesar
  const confirm = window.prompt(
    `Se encontraron ${ids.length} registro(s) en status Billing y se pasarán a "Payed".\n\nEscribe PAYED para confirmar:`
  );
  if (confirm !== "PAYED") return;

  setActionLoading(true);

  const parseMaybeJson = async (resp) => {
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    const raw = await resp.text();
    let json = null;
    if (ct.includes("application/json")) {
      try { json = JSON.parse(raw); } catch {}
    } else {
      try { json = JSON.parse(raw); } catch {}
    }
    return { raw, json };
  };

  const looksSuccess = (obj, raw) => {
    if (obj && typeof obj === "object") {
      const st = String(obj.status ?? obj.result ?? "").toLowerCase();
      if (st === "success" || st === "ok") return true;
      if (obj.success === true || obj.ok === true) return true;
      if (typeof obj.affected !== "undefined" && Number(obj.affected) > 0) return true;
      if (typeof obj.updated !== "undefined" && Number(obj.updated) > 0) return true;
    }
    const s = String(raw || "").toLowerCase();
    if (s.includes("success") || s.includes("updated") || s.includes("ok")) return true;
    if (s.trim() === "1") return true;
    return false;
  };

  const sameOrigin = window.location.origin.replace(/\/+$/, "");
  const endpointCandidates = [
    `${sameOrigin}/api/update_projects.php`,
    `${sameOrigin}/api/update_project.php`,
    `${sameOrigin}/api/update_project_status.php`,
    `${sameOrigin}/api/update_projects_status.php`,
    `${sameOrigin}/api/update_jobsta.php`,
    `${sameOrigin}/api/update_status.php`,
    // fallback (si tu app ya está mismo origen que backend)
    `/update_projects.php`,
    `/update_project.php`,
    `/update_project_status.php`,
    `/update_projects_status.php`,
    `/update_jobsta.php`,
    `/update_status.php`,
  ];

  // Formatos típicos de backend (campo/valor, columna, etc.) -> PAYED
  const payloadVariants = (jobid) => [
    new URLSearchParams({ jobid, jobsta: "Payed" }),
    new URLSearchParams({ jobid, status: "Payed" }),
    new URLSearchParams({ jobid, jobstatus: "Payed" }),
    new URLSearchParams({ jobid, field: "jobsta", value: "Payed" }),
    new URLSearchParams({ jobid, column: "jobsta", value: "Payed" }),
    new URLSearchParams({ jobid, col: "jobsta", val: "Payed" }),
    new URLSearchParams({ id: jobid, field: "jobsta", value: "Payed" }),
    new URLSearchParams({ job_id: jobid, field: "jobsta", value: "Payed" }),
    new URLSearchParams({ jobid, table: "projects", field: "jobsta", value: "Payed" }),
  ];

  // Registro para mostrar diagnóstico
  let lastDiag = { url: "", method: "", payload: "", status: "", snippet: "" };

  const sendOnce = async (url, method, params) => {
    try {
      const fullUrl =
        method === "GET" ? `${url}${url.includes("?") ? "&" : "?"}${params.toString()}` : url;

      const resp = await fetch(fullUrl, {
        method,
        headers:
          method === "POST"
            ? { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" }
            : undefined,
        body: method === "POST" ? params.toString() : undefined,
        credentials: "omit",
      });

      const { raw, json } = await parseMaybeJson(resp);
      const snippet = String(raw || "").replace(/\s+/g, " ").slice(0, 180);

      lastDiag = {
        url: fullUrl,
        method,
        payload: params.toString(),
        status: `HTTP ${resp.status}`,
        snippet,
      };

      return { ok: resp.ok && looksSuccess(json, raw) };
    } catch (e) {
      lastDiag = {
        url,
        method,
        payload: params.toString(),
        status: "NETWORK",
        snippet: String(e?.message || "Network error").slice(0, 180),
      };
      return { ok: false };
    }
  };

  const tryUpdateOne = async (jobid) => {
    for (const ep of endpointCandidates) {
      for (const params of payloadVariants(jobid)) {
        const postRes = await sendOnce(ep, "POST", params);
        if (postRes.ok) return true;

        const getRes = await sendOnce(ep, "GET", params);
        if (getRes.ok) return true;
      }
    }
    return false;
  };

  try {
    // 1) actualizar uno por uno SOLO los Billing
    for (const id of ids) {
      await tryUpdateOne(id);
    }

    // 2) recargar desde backend (GET ya funciona en tu entorno)
    const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
    if (data?.status === "success") {
      const arr = Array.isArray(data.data) ? data.data : [];
      setProjectsRows(arr);

      // 3) verificar cambios SOLO de los que eran Billing
      const afterMap = new Map();
      arr.forEach((r, idx) => afterMap.set(getRowId(r, idx), String(r?.jobsta ?? "")));

      let changed = 0;
      ids.forEach((id) => {
        const b = before.get(id);
        const a = afterMap.get(id);
        if (a === "Payed" && b !== "Payed") changed += 1;
      });

      if (changed > 0) {
        setActionErr(`OK: se pasó a Payed ${changed} de ${ids.length} registro(s) Billing.`);
      } else {
        const diag = lastDiag?.url
          ? ` Último intento: ${lastDiag.method} ${lastDiag.url} | ${lastDiag.status} | ${lastDiag.snippet}`
          : "";
        setActionErr(
          `No se detectó cambio a Payed en los Billing (0 de ${ids.length}). Posible endpoint/param distinto en backend.` +
            diag
        );
      }
    } else {
      setActionErr("No se pudo recargar Projects para verificar el cambio.");
    }
  } catch (e) {
    const diag = lastDiag?.url
      ? ` Último intento: ${lastDiag.method} ${lastDiag.url} | ${lastDiag.status} | ${lastDiag.snippet}`
      : "";
    setActionErr("Error al pasar a Payed (backend no disponible o CORS/proxy)." + diag);
  } finally {
    setActionLoading(false);
  }
};

// Archived: pasa TODOS los registros con jobsta="Payed" a "Archived" (sin mirar seleccionados)
const applyArchivedToAllPayed = async () => {
  if (actionLoading) return;

  // Archived afecta a TODOS los registros con jobsta="Payed"
  const ids = [];
  const before = new Map();

  projectsRows.forEach((r, idx) => {
    const rid = getRowId(r, idx);
    const st = String(r?.jobsta ?? "").trim();
    if (st === "Payed") {
      ids.push(rid);
      before.set(rid, st);
    }
  });

  if (ids.length === 0) {
    setActionErr('Archived: no se encontraron registros con jobsta="Payed".');
    return;
  }

  setActionErr("");

  const confirm = window.prompt(
    `Se encontraron ${ids.length} registro(s) en status Payed y se pasarán a "Archived".\n\nEscribe ARCHIVED para confirmar:`
  );
  if (confirm !== "ARCHIVED") return;

  setActionLoading(true);

  // Helpers (idénticos a Payed, pero cambiando el valor)
  const parseMaybeJson = async (resp) => {
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    const raw = await resp.text();
    let json = null;
    if (ct.includes("application/json")) {
      try {
        json = JSON.parse(raw);
      } catch {}
    } else {
      try {
        json = JSON.parse(raw);
      } catch {}
    }
    return { raw, json };
  };

  const looksSuccess = (obj, raw) => {
    if (obj && typeof obj === "object") {
      const st = String(obj.status ?? obj.result ?? "").toLowerCase();
      if (st === "success" || st === "ok") return true;
      if (obj.success === true || obj.ok === true) return true;
      if (typeof obj.affected !== "undefined" && Number(obj.affected) > 0) return true;
      if (typeof obj.updated !== "undefined" && Number(obj.updated) > 0) return true;
    }
    const s = String(raw || "").toLowerCase();
    if (s.includes("success") || s.includes("updated") || s.includes("ok")) return true;
    if (s.trim() === "1") return true;
    return false;
  };

  const sameOrigin = window.location.origin.replace(/\/+$/, "");
  const endpointCandidates = [
    `${sameOrigin}/api/update_projects.php`,
    `${sameOrigin}/api/update_project.php`,
    `${sameOrigin}/api/update_project_status.php`,
    `${sameOrigin}/api/update_projects_status.php`,
    `${sameOrigin}/api/update_jobsta.php`,
    `${sameOrigin}/api/update_status.php`,
    // fallback (si tu app ya está mismo origen que backend)
    `/update_projects.php`,
    `/update_project.php`,
    `/update_project_status.php`,
    `/update_projects_status.php`,
    `/update_jobsta.php`,
    `/update_status.php`,
  ];

  const payloadVariants = (jobid) => [
    new URLSearchParams({ jobid, jobsta: "Archived" }),
    new URLSearchParams({ jobid, status: "Archived" }),
    new URLSearchParams({ jobid, jobstatus: "Archived" }),
    new URLSearchParams({ jobid, field: "jobsta", value: "Archived" }),
    new URLSearchParams({ jobid, column: "jobsta", value: "Archived" }),
    new URLSearchParams({ jobid, col: "jobsta", val: "Archived" }),
    new URLSearchParams({ id: jobid, field: "jobsta", value: "Archived" }),
    new URLSearchParams({ job_id: jobid, field: "jobsta", value: "Archived" }),
    new URLSearchParams({ jobid, table: "projects", field: "jobsta", value: "Archived" }),
  ];

  let lastDiag = { url: "", method: "", payload: "", status: "", snippet: "" };

  const sendOnce = async (url, method, params) => {
    try {
      const fullUrl =
        method === "GET" ? `${url}${url.includes("?") ? "&" : "?"}${params.toString()}` : url;

      const resp = await fetch(fullUrl, {
        method,
        headers:
          method === "POST"
            ? { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" }
            : undefined,
        body: method === "POST" ? params.toString() : undefined,
        credentials: "omit",
      });

      const { raw, json } = await parseMaybeJson(resp);
      const snippet = String(raw || "").replace(/\s+/g, " ").slice(0, 180);

      lastDiag = {
        url: fullUrl,
        method,
        payload: params.toString(),
        status: `HTTP ${resp.status}`,
        snippet,
      };

      return { ok: resp.ok && looksSuccess(json, raw) };
    } catch (e) {
      lastDiag = {
        url,
        method,
        payload: params.toString(),
        status: "NETWORK",
        snippet: String(e?.message || "Network error").slice(0, 180),
      };
      return { ok: false };
    }
  };

  const tryUpdateOne = async (jobid) => {
    for (const ep of endpointCandidates) {
      for (const params of payloadVariants(jobid)) {
        const postRes = await sendOnce(ep, "POST", params);
        if (postRes.ok) return true;

        const getRes = await sendOnce(ep, "GET", params);
        if (getRes.ok) return true;
      }
    }
    return false;
  };

  try {
    // 1) actualizar uno por uno SOLO los Payed
    for (const id of ids) {
      await tryUpdateOne(id);
    }

    // 2) recargar desde backend
    const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
    if (data?.status === "success") {
      const arr = Array.isArray(data.data) ? data.data : [];
      setProjectsRows(arr);

      // 3) verificar cambios SOLO de los que eran Payed
      const afterMap = new Map();
      arr.forEach((r, idx) => afterMap.set(getRowId(r, idx), String(r?.jobsta ?? "")));

      let changed = 0;
      ids.forEach((id) => {
        const b = before.get(id);
        const a = afterMap.get(id);
        if (a === "Archived" && b !== "Archived") changed += 1;
      });

      if (changed > 0) {
        setActionErr(`OK: se pasó a Archived ${changed} de ${ids.length} registro(s) Payed.`);
      } else {
        const diag = lastDiag?.url
          ? ` Último intento: ${lastDiag.method} ${lastDiag.url} | ${lastDiag.status} | ${lastDiag.snippet}`
          : "";
        setActionErr(
          `No se detectó cambio a Archived en los Payed (0 de ${ids.length}). Posible endpoint/param distinto en backend.` +
            diag
        );
      }
    } else {
      setActionErr("No se pudo recargar Projects para verificar el cambio.");
    }
  } catch (e) {
    const diag = lastDiag?.url
      ? ` Último intento: ${lastDiag.method} ${lastDiag.url} | ${lastDiag.status} | ${lastDiag.snippet}`
      : "";
    setActionErr("Error al pasar a Archived (backend no disponible o CORS/proxy)." + diag);
  } finally {
    setActionLoading(false);
  }
};
const canDownload = true; // habilitado para cualquier Project (filtro por Project se aplica al reporte)

  const handleDownload = async () => {
    if (downloading) return;

    const pass = window.prompt("Password:");
    if (pass !== "Invoicexd") {
      alert("Password incorrecto.");
      return;
    }

    
    if (!chkMain && !chkBentley && !chkMainSFD && !chkBentleySFD) {
      alert("Selecciona al menos una hoja.");
      return;
    }

    setDownloading(true);
    try {
      const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
      if (data?.status === "success") {
        const arr = Array.isArray(data.data) ? data.data : [];
const byProject =
          selected && selected !== "All" && selected !== "Admin"
            ? arr.filter(
                (r) => String(r?.jobpro ?? "").trim() === String(selected).trim()
              )
            : arr;

        const filtered =
          selectedStatus && selectedStatus !== "All"
            ? byProject.filter(
                (r) => String(r?.jobsta ?? "").trim() === String(selectedStatus).trim()
              )
            : byProject;

        const filteredForXLS = filterOutExcludedXLS(filtered);

        exportAdminXLS(filteredForXLS, {
          main: chkMain,
          bentley: chkBentley,
          mainSFD: chkMainSFD,
          bentleySFD: chkBentleySFD,
        });
      } else {
        alert("No se pudo generar el reporte (respuesta no-success).");
      }
    } catch (e) {
      alert("Error de conexión con el servidor.");
    } finally {
      setDownloading(false);
    }
  };
  const handleUnlockSummary = () => {
    if (summaryUnlocked) return;
    const pass = window.prompt("Password:");
    if (pass !== "Invoicexd") {
      alert("Password incorrecto.");
      return;
    }
    setSummaryUnlocked(true);
  };



  // ================= Resumen en pantalla (igual al Excel "Resumen") =================
  const summaryRows = useMemo(() => {
    const flags = {
      main: !!chkMain,
      bentley: !!chkBentley,
      mainSFD: !!chkMainSFD,
      bentleySFD: !!chkBentleySFD,
    };

    const arr = filterOutExcludedSummary(projectsRows);

    const byProject =
      selected && selected !== "All" && selected !== "Admin"
        ? arr.filter((r) => String(r?.jobpro ?? "").trim() === String(selected).trim())
        : arr;

    const filtered =
      selectedStatus && selectedStatus !== "All"
        ? byProject.filter((r) => String(r?.jobsta ?? "").trim() === String(selectedStatus).trim())
        : byProject;

    const isCoaxBentley = (r) => {
    const t = normalize(r?.jobti);
    return t === "coaxbentley" || t === "irissplits" || t === "irissplitcoaxdesign" || t === "bentleysplitcoaxdesign";
  };
    const rowsBentley = filtered.filter((r) => isCoaxBentley(r));
    const rowsMain = filtered.filter((r) => !isCoaxBentley(r));

    const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
    const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
    const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
    const sumBentleySFD = flags.bentleySFD
      ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0))
      : 0;

    const baseTotal = r2(sumMain + sumBentley);
    const sfdTotal = r2(sumMainSFD + sumBentleySFD);
    const hasBaseSelection = !!(flags.main || flags.bentley);
    const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

    const out = [];
    if (flags.main) out.push({ concepto: "Main", total: sumMain });
    if (flags.bentley) out.push({ concepto: "Coax Bentley", total: sumBentley });
    if (flags.mainSFD) out.push({ concepto: "Main-SFD", total: sumMainSFD });
    if (flags.bentleySFD) out.push({ concepto: "Coax Bentley SFD", total: sumBentleySFD });
    out.push({ concepto: "TOTAL FINAL", total: totalFinal, isTotal: true });

    return out;
  }, [projectsRows, selected, selectedStatus, chkMain, chkBentley, chkMainSFD, chkBentleySFD]);


  // ================= Resumen 1: TOTAL FINAL por día (usa jobdatesub) =================
  const dailyTotals = useMemo(() => {
    const flags = {
      main: !!chkMain,
      bentley: !!chkBentley,
      mainSFD: !!chkMainSFD,
      bentleySFD: !!chkBentleySFD,
    };

    const arr = filterOutExcludedSummary(projectsRows);

    const byProject =
      selected && selected !== "All" && selected !== "Admin"
        ? arr.filter((r) => String(r?.jobpro ?? "").trim() === String(selected).trim())
        : arr;

    const filtered =
      selectedStatus && selectedStatus !== "All"
        ? byProject.filter((r) => String(r?.jobsta ?? "").trim() === String(selectedStatus).trim())
        : byProject;

    const isCoaxBentley = (r) => {
    const t = normalize(r?.jobti);
    return t === "coaxbentley" || t === "irissplits" || t === "irissplitcoaxdesign" || t === "bentleysplitcoaxdesign";
  };

    const totalFinalForRows = (rows) => {
      const rowsBentley = rows.filter((r) => isCoaxBentley(r));
      const rowsMain = rows.filter((r) => !isCoaxBentley(r));

      const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
      const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
      const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
      const sumBentleySFD = flags.bentleySFD
        ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0))
        : 0;

      const baseTotal = r2(sumMain + sumBentley);
      const sfdTotal = r2(sumMainSFD + sumBentleySFD);
      const hasBaseSelection = !!(flags.main || flags.bentley);
      const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

      return totalFinal;
    };

    const map = new Map();
    filtered.forEach((r) => {
      const k = getDateKey(r?.jobdatesub);
      if (!k) return;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });

    const rows = Array.from(map.entries())
      .map(([day, rows]) => ({ day, total: totalFinalForRows(rows) }))
      .sort((a, b) => String(b.day).localeCompare(String(a.day)));

    const grandTotal = r2(rows.reduce((acc, r) => acc + Number(r.total || 0), 0));

    return { rows, grandTotal };
  }, [projectsRows, selected, selectedStatus, chkMain, chkBentley, chkMainSFD, chkBentleySFD]);

  // ================= Resumen 2: Hoy por jobpro (usa jobdatesub) =================
  const todayByProject = useMemo(() => {
    const flags = {
      main: !!chkMain,
      bentley: !!chkBentley,
      mainSFD: !!chkMainSFD,
      bentleySFD: !!chkBentleySFD,
    };

    const arr = filterOutExcludedSummary(projectsRows);

    const baseRows = arr; // para este cuadro mostramos TODOS los projects

    const filtered =
      selectedStatus && selectedStatus !== "All"
        ? baseRows.filter((r) => String(r?.jobsta ?? "").trim() === String(selectedStatus).trim())
        : baseRows;

    const isCoaxBentley = (r) => {
    const t = normalize(r?.jobti);
    return t === "coaxbentley" || t === "irissplits" || t === "irissplitcoaxdesign" || t === "bentleysplitcoaxdesign";
  };

    const totalFinalForRows = (rows) => {
      const rowsBentley = rows.filter((r) => isCoaxBentley(r));
      const rowsMain = rows.filter((r) => !isCoaxBentley(r));

      const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
      const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
      const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
      const sumBentleySFD = flags.bentleySFD
        ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0))
        : 0;

      const baseTotal = r2(sumMain + sumBentley);
      const sfdTotal = r2(sumMainSFD + sumBentleySFD);
      const hasBaseSelection = !!(flags.main || flags.bentley);
      const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

      return totalFinal;
    };

    const todayKey = getDateKey(new Date());
    const targetKey = processDate || todayKey;

    const todaysRows = filtered.filter((r) => getDateKey(r?.jobdatesub) === targetKey);

    const PROJECTS = [
      "Project-01",
      "Project-02",
      "Project-03",
      "Project-04",
      "Project-05",
      "Project-06",
      "Project-07",
      "Project-08",
    ];

    const map = new Map(PROJECTS.map((p) => [p, []]));
    todaysRows.forEach((r) => {
      const k = String(r?.jobpro ?? "").trim();
      if (!k) return;
      if (!map.has(k)) return; // ignoramos projects fuera del listado
      map.get(k).push(r);
    });

    const rows = PROJECTS.map((jobpro) => {
      const rowsP = map.get(jobpro) || [];
      return { jobpro, count: rowsP.length, total: totalFinalForRows(rowsP) };
    });

    const dayTotal = r2(rows.reduce((acc, r) => acc + Number(r.total || 0), 0));

    const dayCount = rows.reduce((acc, r) => acc + Number(r.count || 0), 0);

    return { dateKey: targetKey, rows, dayTotal, dayCount };
  }, [projectsRows, selected, selectedStatus, chkMain, chkBentley, chkMainSFD, chkBentleySFD, processDate]);

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 12 }}>Reportes</h2>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ maxWidth: 260 }}>
        <select
          value={selected}
          onChange={(e) => {
            const v = e.target.value;
            setSelected(v);
            if (v !== "Admin") setSelectedStatus("All");
          }}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            fontSize: 14,
          }}
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      
<div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
  <label style={{ display: "flex", gap: 6, alignItems: "center", userSelect: "none" }}>
    <input type="checkbox" checked={chkMain} onChange={(e) => setChkMain(e.target.checked)} />
    Main
  </label>

  <label style={{ display: "flex", gap: 6, alignItems: "center", userSelect: "none" }}>
    <input
      type="checkbox"
      checked={chkBentley}
      onChange={(e) => setChkBentley(e.target.checked)}
    />
    Coax Bentley
  </label>

  <label style={{ display: "flex", gap: 6, alignItems: "center", userSelect: "none" }}>
    <input
      type="checkbox"
      checked={chkMainSFD}
      onChange={(e) => setChkMainSFD(e.target.checked)}
    />
    Main SFD
  </label>

  <label style={{ display: "flex", gap: 6, alignItems: "center", userSelect: "none" }}>
    <input
      type="checkbox"
      checked={chkBentleySFD}
      onChange={(e) => setChkBentleySFD(e.target.checked)}
    />
    Coax Bentley SFD
  </label>
</div>

</div>


        
          <div style={{ maxWidth: 260 }}>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: 14,
              }}
            >
              {statusOptions.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>

        {/* ===================== Resumen (protegido por password) ===================== */}
        <div style={{ minWidth: 300, maxWidth: 420 }}>
          <button
            type="button"
            onClick={handleUnlockSummary}
            disabled={summaryUnlocked}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #6E4A26",
              background: summaryUnlocked ? "#f1f1f1" : "#FFF7E6",
              cursor: summaryUnlocked ? "default" : "pointer",
              fontWeight: 800,
            }}
            title="Requiere password para mostrar el resumen"
          >
            {summaryUnlocked ? "Resumen habilitado" : "Mostrar resumen"}
          </button>

          {summaryUnlocked && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 16 }}>
              {/** ====== Resumen (igual Excel) ====== **/}
              <div style={{ border: "2px solid #6E4A26", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ background: "#8B5A2B", color: "white", fontWeight: 900, padding: "8px 10px" }}>
                  Resumen
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: "#C98B5A", color: "white" }}>
                      <th style={{ textAlign: "left", padding: "8px 10px" }}>Concepto</th>
                      <th style={{ textAlign: "right", padding: "8px 10px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((r, idx) => (
                      <tr
                        key={idx}
                        style={{
                          background: r.isTotal ? "#8B5A2B" : "#FFF3E6",
                          color: r.isTotal ? "white" : "#2b2b2b",
                          fontWeight: r.isTotal ? 900 : 600,
                          borderTop: "1px solid #e0c2a8",
                        }}
                      >
                        <td style={{ padding: "8px 10px" }}>{r.concepto}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          {Number(r.total || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/** ====== 1) TOTAL FINAL por día ====== **/}
                  <div style={{ border: "2px solid #6E4A26", borderRadius: 10, overflow: "hidden", minWidth: 320, maxWidth: 460 }}>
                    <div style={{ background: "#8B5A2B", color: "white", fontWeight: 900, padding: "8px 10px" }}>
                      TOTAL FINAL por día
                    </div>

                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ background: "#C98B5A", color: "white" }}>
                          <th style={{ textAlign: "left", padding: "8px 10px" }}>Día</th>
                          <th style={{ textAlign: "right", padding: "8px 10px" }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyTotals.rows.length === 0 ? (
                          <tr style={{ background: "#FFF3E6", borderTop: "1px solid #e0c2a8" }}>
                            <td style={{ padding: "8px 10px" }} colSpan={2}>
                              Sin datos
                            </td>
                          </tr>
                        ) : (
                          <>
                            {dailyTotals.rows.map((r, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  background: "#FFF3E6",
                                  color: "#2b2b2b",
                                  fontWeight: 700,
                                  borderTop: "1px solid #e0c2a8",
                                }}
                              >
                                <td style={{ padding: "8px 10px" }}>{r.day}</td>
                                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                  {Number(r.total || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}

                            <tr
                              style={{
                                background: "#8B5A2B",
                                color: "white",
                                fontWeight: 900,
                                borderTop: "1px solid #e0c2a8",
                              }}
                            >
                              <td style={{ padding: "8px 10px" }}>TOTAL</td>
                              <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                {Number(dailyTotals.grandTotal || 0).toFixed(2)}
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

            </div>
    
          )}
        </div>

        {summaryUnlocked && (
          <div style={{ minWidth: 340, maxWidth: 460, marginLeft: "auto" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/** ====== Fecha a procesar ====== **/}
              <div style={{ border: "2px solid #6E4A26", borderRadius: 10, overflow: "hidden", minWidth: 320, maxWidth: 460 }}>
                <div style={{ background: "#8B5A2B", color: "white", fontWeight: 900, padding: "8px 10px" }}>
                  Fecha a procesar
                </div>
                <div style={{ background: "#FFF3E6", padding: 10 }}>
                  <input
                    type="date"
                    value={processDate}
                    onChange={(e) => setProcessDate(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #6E4A26",
                      fontSize: 14,
                      fontWeight: 800,
                      background: "#FFF7E6",
                    }}
                  />
                </div>
              </div>

              {/** ====== 2) Hoy por jobpro ====== **/}
                  <div style={{ border: "2px solid #6E4A26", borderRadius: 10, overflow: "hidden", minWidth: 320, maxWidth: 460 }}>
                    <div style={{ background: "#8B5A2B", color: "white", fontWeight: 900, padding: "8px 10px" }}>
                      Por Project ({todayByProject.dateKey})
                    </div>

                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <thead>
                        <tr style={{ background: "#C98B5A", color: "white" }}>
                          <th style={{ textAlign: "left", padding: "8px 10px" }}>Project</th>
                          <th style={{ textAlign: "center", padding: "8px 10px" }}>Registros</th>
                          <th style={{ textAlign: "right", padding: "8px 10px" }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayByProject.rows.length === 0 ? (
                          <tr style={{ background: "#FFF3E6", borderTop: "1px solid #e0c2a8" }}>
                            <td style={{ padding: "8px 10px" }} colSpan={3}>
                              Sin datos para hoy
                            </td>
                          </tr>
                        ) : (
                          <>
                            {todayByProject.rows.map((r, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  background: "#FFF3E6",
                                  color: "#2b2b2b",
                                  fontWeight: 700,
                                  borderTop: "1px solid #e0c2a8",
                                }}
                              >
                                <td style={{ padding: "8px 10px" }}>{r.jobpro}</td>
                                <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                  {Number(r.count || 0)}
                                </td>
                                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                  {Number(r.total || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}

                            <tr
                              style={{
                                background: "#8B5A2B",
                                color: "white",
                                fontWeight: 900,
                                borderTop: "1px solid #e0c2a8",
                              }}
                            >
                              <td style={{ padding: "8px 10px" }}>TOTAL HOY</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                {Number(todayByProject.dayCount || 0)}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "right" }}>
                                {Number(todayByProject.dayTotal || 0).toFixed(2)}
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
            </div>
          </div>
        )}



      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
        <button
          onClick={handleDownload}
          disabled={downloading}
          style={{
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid #7aa7d9",
            background: canDownload ? "#d9ecff" : "#f1f1f1",
            cursor: canDownload ? "pointer" : "not-allowed",
            fontWeight: 600,
            opacity: downloading ? 0.7 : 1,
          }}
          title="Descargar reporte (filtra por Project y Status)"
        >
          {downloading ? "Downloading..." : "Download"}
        </button>

        <div style={{ fontSize: 14 }}>
          Seleccionado: <b>{selected}</b>
        </div>
      </div>

      {/* ===================== GRID Projects (Solo lectura) ===================== */}
      <div
        style={{
          marginTop: 16,
          border: "3px solid #e00000",
          borderRadius: 6,
          padding: 10,
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 700 }}>Projects (solo lectura)</div>
          <div style={{ fontSize: 13 }}>
            Filas: <b>{projectsRows.length}</b> | Seleccionadas: <b>{selectedRowIds.size}</b>
          </div>

          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={isReadyForQcSelected}
              onChange={toggleReadyForQc}
              disabled={readyForQcRowIds.length === 0}
              title='Seleccionar todos los jobsta="Ready for QC"'
            />
            Ready for QC
          </label>


          <button
            type="button"
            disabled={selectedRowIds.size === 0 || actionLoading}
            style={{
              padding: "7px 10px",
              borderRadius: 6,
              border: "1px solid #b9b9b9",
              background: (selectedRowIds.size === 0 || actionLoading) ? "#f1f1f1" : "#eee",
              cursor: (selectedRowIds.size === 0 || actionLoading) ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
            title="Pasar seleccionados a Billing"
            onClick={applyBillingToSelected}
          >
            {actionLoading ? "Aplicando Billing..." : "Billing"}
          </button>

<button
  type="button"
  disabled={actionLoading}
  style={{
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #b9b9b9",
    background: actionLoading ? "#f1f1f1" : "#eee",
    cursor: actionLoading ? "not-allowed" : "pointer",
    fontWeight: 600,
  }}
  title='Pasar TODOS los registros con jobsta="Billing" a Payed'
  onClick={applyPayedToSelectedBilling}
>
  {actionLoading ? "Aplicando Payed..." : "Payed"}
</button>

	<button
	  type="button"
	  disabled={actionLoading}
	  style={{
	    padding: "7px 10px",
	    borderRadius: 6,
	    border: "1px solid #b9b9b9",
	    background: actionLoading ? "#f1f1f1" : "#eee",
	    cursor: actionLoading ? "not-allowed" : "pointer",
	    fontWeight: 600,
	  }}
	  title='Pasar TODOS los registros con jobsta="Payed" a Archived'
	  onClick={applyArchivedToAllPayed}
	>
	  {actionLoading ? "Aplicando Archived..." : "Archived"}
	</button>

          {projectsLoading && <div style={{ fontSize: 13 }}>Cargando...</div>}
          {!!projectsErr && <div style={{ fontSize: 13, color: "#b00020" }}>{projectsErr}</div>}
          {!!actionErr && <div style={{ fontSize: 13, color: "#b00020" }}>{actionErr}</div>}
        </div>

        <div style={{ height: 560, overflow: "auto", border: "1px solid #e3e3e3", borderRadius: 6 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f6f6f6", zIndex: 2 }}>
                <th style={{ borderBottom: "1px solid #ddd", padding: "8px 10px", textAlign: "center", width: 44 }}>
                  <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} />
                </th>

                {columns.map((c) => (
                  <th
                    key={c}
                    style={{
                      borderBottom: "1px solid #ddd",
                      padding: "8px 10px",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c === "jbhrex" ? "Hr Extras" : (billingDetailLabels[c] || c)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {projectsRows.map((r, idx) => {
                const rowId = getRowId(r, idx);
                const isChecked = selectedRowIds.has(rowId);
                const isAlt = idx % 2 === 1;

                return (
                  <tr key={rowId} style={{ background: isAlt ? "#fbfbfb" : "#fff" }}>
                    <td style={{ borderBottom: "1px solid #eee", padding: "8px 10px", textAlign: "center" }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleRow(rowId)} />
                    </td>

                    {columns.map((c) => (
                      <td
                        key={c}
                        style={{
                          borderBottom: "1px solid #eee",
                          padding: "8px 10px",
                          whiteSpace: "nowrap",
                        }}
                        title={billingDetailValue(r, c)}
                      >
                        {billingDetailValue(r, c)}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {!projectsLoading && projectsRows.length === 0 && (
                <tr>
                  <td colSpan={1 + columns.length} style={{ padding: 14, textAlign: "center", color: "#666" }}>
                    No hay datos para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
